import { PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EntityMeta, PickLabel } from "@wms/domain";
import { App, Button, Divider, Select, Spin, Tooltip, Typography } from "antd";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { Page } from "../api/client";
import { useAuthStore } from "../auth/store";
import { CreateDrawer } from "./CreateDrawer";
import { withArchive } from "./refOptions";

type Row = Record<string, unknown>;

/** Human label for a reference row: code + name, or the first sensible text field. */
export function rowLabel(row: Row | undefined, pick?: PickLabel | null): string {
  if (!row) return "";
  if (pick) return pickWords(row, pick);
  const code = row.code ?? row.number ?? row.username ?? row.barcode;
  const name = row.name ?? row.full_name;
  // technical identifiers (internal, placement, dispatcher …) add nothing next to the name; article-like codes do
  if (code && name) return /^[a-z_]+$/.test(String(code)) ? String(name) : `${code} — ${name}`;
  // UX-forms section 15.2: a row without a readable name is never shown as its id
  return String(name ?? code ?? (row.id ? "Название недоступно" : ""));
}

/**
 * Section 8.1 of the UX spec: the same words the server builds in «/refs/labels» from the entity's `pick_label` —
 * «Альфа Торг · БИН 123…» (ТОО) or «· ИИН 123…» (ИП), «SKU-1 — Молоко 1л · арт. A-77». The internal code of a partner
 * and the ids stay out; the ИНН of an older card is never named (KZ 30.10.2026, R04).
 */
export function pickWords(row: Row, pick: PickLabel): string {
  const primary = pick.primary.map((f) => row[f]).filter((v) => v !== null && v !== undefined && v !== "").map(String);
  const extras: string[] = [];
  for (const item of pick.secondary ?? []) {
    const [field, word] = secondaryWord(item, row);
    const value = row[field];
    if (value !== null && value !== undefined && value !== "") extras.push(`${word} ${value}`.trim());
  }
  if (!extras.length && pick.missing) extras.push(pick.missing);
  const words = [...new Set(primary)].join(" — ") || pick.nameless || "Название недоступно";
  return extras.length ? [words, ...extras].join(" · ") : words;
}

/** A secondary part of a pick label: `"field"`, `["field", "word"]` or a word that follows another field of the row. */
export function secondaryWord(item: NonNullable<PickLabel["secondary"]>[number], row: Row): [string, string] {
  if (Array.isArray(item)) return [item[0], item[1]];
  if (typeof item === "string") return [item, ""];
  const word = item.by ? item.words?.[String(row[item.by] ?? "")] ?? item.word ?? "" : item.word ?? "";
  return [item.field, word];
}

/** «Холодильная камера 2–8 °C», «Хранение: паллетные стеллажи (15…25 °C)»: the zone of a cell as a choice names it,
 * so a cold cell and an ordinary one are told apart before the choice. */
export function zoneWords(zone: Row): string {
  const t = (v: unknown) => String(Number(v)).replace("-", "−");
  const lo = zone.temperature_min ?? null;
  const hi = zone.temperature_max ?? null;
  const name = String(zone.name ?? zone.code ?? "");
  if ((lo === null && hi === null) || name.includes("°C")) return name;
  const range = lo !== null && hi !== null ? `${t(lo)}…${t(hi)} °C` : lo !== null ? `от ${t(lo)} °C` : `до ${t(hi)} °C`;
  return `${name} (${range})`;
}

export function useRefRows(entity: string | undefined, q?: string, filter?: Record<string, unknown> | null, lookup?: "pick") {
  return useQuery({
    queryKey: ["ref", entity, q ?? "", filter ?? null, lookup ?? ""],
    queryFn: () => api.get<Page<Row>>(`/${entity}`, { size: 200, q, filter: filter ?? undefined, lookup: q ? lookup : undefined }),
    enabled: !!entity,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Meta of a referenced catalogue: how its rows are named, whether «+» may create one, which right it needs. */
export function useEntityMeta(entity: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["meta", entity],
    queryFn: () => api.get<EntityMeta>(`/${entity}/meta`),
    enabled: enabled && !!entity,
    staleTime: 300_000,
    retry: false,
  });
}

/**
 * D12: display names by id, batched. Table cells and selects ask for the names they could not find in the loaded list
 * (another page, or a list the role may not open — the operator's warehouse or executor); one request per entity per
 * tick answers all of them.
 */
const pending = new Map<string, Map<string, Array<(label: string | null) => void>>>();
let flushTimer: number | undefined;

function flushLabels() {
  flushTimer = undefined;
  const batch = new Map(pending);
  pending.clear();
  for (const [entity, waiters] of batch) {
    const ids = [...waiters.keys()];
    for (let i = 0; i < ids.length; i += 200) {
      const chunk = ids.slice(i, i + 200);
      api
        .get<Array<{ id: string; label: string }>>("/refs/labels", { entity, ids: chunk.join(",") })
        .then((rows) => {
          const found = new Map(rows.map((r) => [r.id, r.label]));
          for (const id of chunk) for (const resolve of waiters.get(id) ?? []) resolve(found.get(id) ?? null);
        })
        .catch(() => {
          for (const id of chunk) for (const resolve of waiters.get(id) ?? []) resolve(null);
        });
    }
  }
}

export function fetchLabel(entity: string, id: string): Promise<string | null> {
  return new Promise((resolve) => {
    const byId = pending.get(entity) ?? new Map<string, Array<(label: string | null) => void>>();
    byId.set(id, [...(byId.get(id) ?? []), resolve]);
    pending.set(entity, byId);
    if (flushTimer === undefined) flushTimer = window.setTimeout(flushLabels, 15);
  });
}

/**
 * CW31-B22: server-side names of the loaded rows the generic «code — name» rule cannot name (order lines): one batched
 * «/refs/labels» request — the words the chosen value already had, «B000008 · стр. 1 · CUP31 … · план 20 / отобрано 12».
 * The names carry quantities, so they are asked again whenever the list is shown anew.
 */
export function useServerLabels(entity: string | undefined, ids: string[]) {
  return useQuery({
    queryKey: ["ref-labels", entity, ids.join(",")],
    queryFn: async () => Object.fromEntries(await Promise.all(ids.map(async (id) => [id, await fetchLabel(entity!, id)] as const))),
    enabled: !!entity && ids.length > 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** Server-side display name of one reference; only runs when `enabled` (the local list did not have it). */
export function useServerLabel(entity: string | undefined, id: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["ref-label", entity, id],
    queryFn: () => fetchLabel(entity!, id!),
    enabled: enabled && !!entity && !!id && UUID_RE.test(String(id)),
    staleTime: 300_000,
  });
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A row the generic «code — name» rule cannot describe (document lines have neither). */
function needsServerLabel(row: Row | undefined): boolean {
  return !row || !(row.code ?? row.number ?? row.username ?? row.barcode ?? row.name ?? row.full_name);
}

/** The typed text, settled: a scanner or a fast typist no longer fires a request per keystroke (WMS-05). */
function useDebounced(value: string, delay = 250): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

/** The typed search text becomes the one field it clearly matches: a name, or a 12-digit БИН (section 7.1). */
function prefillFromSearch(text: string, meta: EntityMeta | undefined): Record<string, string> {
  const names = new Set((meta?.fields ?? []).map((f) => f.name));
  if (!text) return {};
  if (/^\d{12}$/.test(text) && names.has("bin")) return { bin: text };
  if (/^\d+$/.test(text)) return {};
  if (names.has("name")) return { name: text };
  return {};
}

export const MAX_CREATE_DEPTH = 2;

interface Props {
  entity: string;
  valueField?: "id" | "code";
  multiple?: boolean;
  value?: string | string[];
  onChange?: (value: string | string[] | undefined) => void;
  disabled?: boolean;
  /** Extra condition on the list, e.g. { field: "room_id", op: "eq", value } (dependent references, WMS-03). */
  filter?: Record<string, unknown> | null;
  /** Shown instead of «Ничего не найдено» when the (filtered) list is empty. */
  emptyHint?: string;
  placeholder?: string;
  /** Rows with these ids are listed first (stock-aware choices in action modals). */
  prefer?: Record<string, string>;
  /** Static condition from the field meta, combined with `filter`. */
  staticFilter?: Record<string, unknown> | null;
  /** Set by Form.Item: ties the field's label to this control (screen readers, «click the label»). */
  id?: string;
  "aria-label"?: string;
  style?: React.CSSProperties;
  /** UI-15: «+» inside the open list creates a row of this catalogue and selects it; needs the create right. */
  create?: boolean;
  /** Values the new row starts with (the chosen partner for a contract, the product for a package), section 7.3. */
  createParams?: Record<string, string>;
  /** Words for the parent the new row must belong to («контрагента»), for the message when it does not. */
  createParentWord?: string;
  /** How many create drawers are already open above this control (two levels at most, section 7.4). */
  depth?: number;
  /** Called with the created row after it was selected. */
  onCreated?: (row: Row) => void;
}

export function RefSelect({
  entity,
  valueField = "id",
  multiple,
  value,
  onChange,
  disabled,
  filter,
  emptyHint,
  placeholder,
  prefer,
  staticFilter,
  id,
  "aria-label": ariaLabel,
  style,
  create,
  createParams,
  createParentWord,
  depth = 0,
  onCreated,
}: Props) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState<Record<string, string> | null>(null);
  const [created, setCreated] = useState<Row[]>([]);
  const settled = useDebounced(search.trim());
  const { message } = App.useApp();
  const can = useAuthStore((s) => s.can);
  const queryClient = useQueryClient();
  const combined = filter && staticFilter ? { and: [filter, staticFilter] } : filter ?? staticFilter ?? null;
  // the meta of the catalogue names its rows and says whether «+» is allowed; a role without read on it gets no meta
  const meta = useEntityMeta(entity);
  const pick = meta.data?.pick_label ?? null;
  const rows = useRefRows(entity, settled || undefined, combined, meta.data?.lookup ? "pick" : undefined);
  // a cell is chosen with its zone and temperature: «Х-01-1 — Холодильная камера 2–8 °C»
  const zones = useRefRows(entity === "locations" ? "zones" : undefined);
  const zoneById = new Map((zones.data?.items ?? []).map((z) => [String(z.id), z]));
  const loaded = rows.data?.items ?? [];
  // a row created from «+» is listed at once, before the list is fetched again
  const items = [...created.filter((c) => !loaded.some((r) => String(r.id) === String(c.id))), ...loaded];
  // CW31-B22: rows without a readable name (order lines) are named by the server, never all «Название недоступно»
  const nameless = valueField === "id" && !pick && !meta.isLoading ? items.filter((r) => r.id && needsServerLabel(r)).map((r) => String(r.id)) : [];
  const serverNames = useServerLabels(entity, nameless);
  const labelOf = (row: Row) => {
    const zone = entity === "locations" && row.zone_id ? zoneById.get(String(row.zone_id)) : undefined;
    const named = row.id && needsServerLabel(row) ? serverNames.data?.[String(row.id)] : undefined;
    const base = named ?? (row.id && needsServerLabel(row) && serverNames.isFetching ? "…" : rowLabel(row, pick));
    return zone ? `${base} — ${zoneWords(zone)}` : base;
  };
  const options = items.map((row) => {
    const key = String(row[valueField]);
    const extra = prefer?.[key];
    return { value: key, label: extra ? `${labelOf(row)} · ${extra}` : labelOf(row), weight: extra ? 0 : 1 };
  });
  if (prefer) options.sort((a, b) => a.weight - b.weight);
  // the chosen value is not on the loaded page (or the role cannot open the list): show its name, not the id
  const single = !multiple && typeof value === "string" ? value : undefined;
  const missing = !!single && valueField === "id" && !rows.isLoading && !options.some((o) => o.value === single);
  const serverLabel = useServerLabel(entity, single, missing);
  // a cell off the loaded page still says its zone and temperature
  const missingCell = useQuery({
    queryKey: ["ref-row", entity, single],
    queryFn: () => api.get<Row>(`/${entity}/${single}`),
    enabled: missing && entity === "locations" && UUID_RE.test(String(single)),
    staleTime: 60_000,
  });
  if (missing && single)
    options.unshift({ value: single, label: missingCell.data ? labelOf(missingCell.data) : serverLabel.data ?? (serverLabel.isFetching ? "…" : "Название недоступно"), weight: 0 });

  // UI-15 / section 7: «+» lives in the service area of the open list, with the list full or empty alike
  const target = meta.data;
  const createAllowed = !!create && !!target && target.inline_create !== false && !target.readonly && !disabled && valueField === "id" && !multiple;
  const createRight = !!target && can(`${target.permission}.write`);
  const tooDeep = depth >= MAX_CREATE_DEPTH;
  const createWord = target ? (target.create_title ?? `Создать: ${target.label.toLowerCase()}`) : "Создать";
  // UX3010-07: the «+» says «Создать услугу» (the drawer keeps the catalogue's own title, «Добавить услугу»)
  const plusWord = createWord.replace(/^Добавить\s+/, "Создать ");
  const createBlocked = !createRight
    ? `Нет права добавлять: ${target?.label_plural.toLowerCase() ?? entity}. Обратитесь к администратору; выбрать существующую запись можно.`
    : tooDeep
      ? "Вложенность ограничена двумя уровнями: откройте раздел справочника и создайте запись там"
      : null;

  const afterCreate = (row: Row) => {
    // section 7.3 / AT-29: the parent may have changed while the child was being saved — a row of another parent
    // is never slipped into the field
    for (const [key, expected] of Object.entries(createParams ?? {})) {
      if (key in row && String(row[key] ?? "") !== String(expected)) {
        message.warning(`Запись «${labelOf(row)}» не относится к выбранному ${createParentWord ?? "значению"}: выберите её заново после проверки`, 8);
        setCreating(null);
        return;
      }
    }
    setCreated((prev) => [row, ...prev]);
    onChange?.(String(row.id));
    queryClient.invalidateQueries({ queryKey: ["ref", entity] });
    queryClient.invalidateQueries({ queryKey: [entity] });
    setCreating(null);
    setSearch("");
    onCreated?.(row);
  };

  const footer = createAllowed ? (
    <>
      <Divider style={{ margin: "6px 0" }} />
      <div
        data-testid={`ref-create-${entity}`}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 8px 4px" }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <Tooltip title={createBlocked ?? plusWord}>
          <Button
            type="default"
            size="small"
            icon={<PlusOutlined />}
            aria-label={plusWord}
            disabled={!!createBlocked}
            onClick={() => {
              setOpen(false);
              setCreating({ ...prefillFromSearch(settled, target), ...(createParams ?? {}) });
            }}
          />
        </Tooltip>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          {createBlocked ?? `Нет нужной позиции? ${plusWord}`}
        </Typography.Text>
      </div>
    </>
  ) : null;

  return (
    <>
      <Select
        id={id}
        aria-label={ariaLabel}
        style={style}
        showSearch
        allowClear
        mode={multiple ? "multiple" : undefined}
        value={value}
        onChange={(v) => onChange?.(v)}
        onSearch={setSearch}
        onBlur={() => setSearch("")}
        open={open}
        onOpenChange={setOpen}
        filterOption={false}
        loading={rows.isFetching}
        options={withArchive(options, items, meta.data?.archive_field, valueField)}
        disabled={disabled}
        // KZ-07 (ТЗ реквизитов §7.4): the hint of the catalogue names only what its search really looks through
        placeholder={placeholder ?? target?.pick_hint ?? "Начните вводить код или название"}
        // section 8.1: a long name is read whole in the list, not guessed from its cut end
        popupMatchSelectWidth={false}
        styles={{ popup: { root: { maxWidth: "min(640px, calc(100vw - 32px))" } } }}
        // UX3010-07: an empty list and «не найдено» point at the «+» right below them when the role may create
        notFoundContent={
          rows.isFetching ? <Spin size="small" /> : emptyHint ?? `${settled ? `«${settled}» не найдено` : "Список пуст"}${createAllowed && !createBlocked ? " — создайте через «+» ниже" : ""}`
        }
        popupRender={footer ? (menu) => <>{menu}{footer}</> : undefined}
      />
      {creating && target && (
        <CreateDrawer entity={entity} title={createWord} prefill={creating} depth={depth + 1} onClose={() => setCreating(null)} onCreated={afterCreate} />
      )}
    </>
  );
}

/** Renders a reference id as its label inside tables. */
export function RefLabel({ entity, id, valueField = "id" }: { entity: string; id: string | null | undefined; valueField?: "id" | "code" }) {
  const rows = useRefRows(entity);
  const meta = useEntityMeta(entity);
  const pick = meta.data?.pick_label ?? null;
  const row = id ? rows.data?.items.find((r) => String(r[valueField]) === String(id)) : undefined;
  const ask = !!id && valueField === "id" && !rows.isLoading && needsServerLabel(row);
  const server = useServerLabel(entity, id, ask);
  if (!id) return null;
  if (ask) return <span>{server.data ?? (server.isFetching || server.isLoading ? "…" : row ? rowLabel(row, pick) : "запись недоступна")}</span>;
  if (!row) return <span>{rows.isLoading ? "…" : "запись недоступна"}</span>;
  return <span>{valueField === "code" ? String(row.name ?? row.full_name ?? rowLabel(row, pick)) : rowLabel(row, pick)}</span>;
}

interface EntityListRow {
  name: string;
  label: string;
  label_plural: string;
  section: string;
}

export function useEntityList() {
  return useQuery({ queryKey: ["meta", "entities"], queryFn: () => api.get<EntityListRow[]>("/meta/entities"), staleTime: 300_000 });
}

/** Human name of a registry list (audit journal, files, attribute definitions). */
export function EntityLabel({ name }: { name: string | null | undefined }) {
  const entities = useEntityList();
  if (!name) return null;
  const row = entities.data?.find((e) => e.name === name);
  return <span>{row ? row.label_plural : name}</span>;
}

export function EntitySelect({ value, onChange, disabled }: { value?: string; onChange?: (v: string | undefined) => void; disabled?: boolean }) {
  const entities = useEntityList();
  return (
    <Select
      showSearch
      allowClear
      optionFilterProp="label"
      value={value}
      onChange={onChange}
      disabled={disabled}
      loading={entities.isLoading}
      options={(entities.data ?? []).map((e) => ({ value: e.name, label: `${e.label_plural} (${e.name})` }))}
    />
  );
}
